import type { SupplyDemandData } from '../types';

/**
 * 供需分析工具函数
 * 
 * 功能：
 * - 计算供需缺口
 * - 计算库存消费比
 * - 数据聚合和统计
 * 
 * 验证需求：9.4
 * 属性12：供需指标计算正确性
 */

/**
 * 计算供需缺口
 * 供需缺口 = 供应量 - 需求量
 * 正值表示供应过剩，负值表示供应不足
 * 
 * @param supply 供应量（吨）
 * @param demand 需求量（吨）
 * @returns 供需缺口（吨）
 */
export function calculateSupplyDemandGap(supply: number, demand: number): number {
  return supply - demand;
}

/**
 * 计算库存消费比
 * 库存消费比 = 库存量 / 月均消费量
 * 表示当前库存可以支撑多少个月的消费
 * 
 * @param inventory 库存量（吨）
 * @param monthlyConsumption 月均消费量（吨/月）
 * @returns 库存消费比（月数）
 */
export function calculateInventoryConsumptionRatio(
  inventory: number,
  monthlyConsumption: number
): number {
  if (monthlyConsumption === 0) {
    return 0;
  }
  return inventory / monthlyConsumption;
}

/**
 * 计算月均消费量
 * 
 * @param annualDemand 年度需求量（吨）
 * @returns 月均消费量（吨/月）
 */
export function calculateMonthlyConsumption(annualDemand: number): number {
  return annualDemand / 12;
}

/**
 * 计算供需比率
 * 供需比率 = 供应量 / 需求量
 * 
 * @param supply 供应量（吨）
 * @param demand 需求量（吨）
 * @returns 供需比率
 */
export function calculateSupplyDemandRatio(supply: number, demand: number): number {
  if (demand === 0) {
    return 0;
  }
  return supply / demand;
}

/**
 * 按应用领域聚合需求数据
 * 
 * @param data 供需数据数组
 * @returns 按应用领域分组的需求数据
 */
export function aggregateDemandByApplication(
  data: SupplyDemandData[]
): Record<string, number> {
  const result: Record<string, number> = {};

  data.forEach(item => {
    if (item.demand_by_application) {
      Object.entries(item.demand_by_application).forEach(([app, demand]) => {
        result[app] = (result[app] || 0) + demand;
      });
    }
  });

  return result;
}

/**
 * 按国家聚合供应数据
 * 
 * @param data 供需数据数组
 * @returns 按国家分组的供应数据
 */
export function aggregateSupplyByCountry(
  data: SupplyDemandData[]
): Array<{ country: string; supply: number }> {
  const countrySupply: Record<string, number> = {};

  data.forEach(item => {
    countrySupply[item.country] = (countrySupply[item.country] || 0) + item.supply_tons;
  });

  return Object.entries(countrySupply)
    .map(([country, supply]) => ({ country, supply }))
    .sort((a, b) => b.supply - a.supply);
}

/**
 * 计算供需趋势数据
 * 
 * @param data 供需数据数组（按年份排序）
 * @returns 供需趋势数据
 */
export function calculateSupplyDemandTrend(
  data: SupplyDemandData[]
): Array<{
  year: number;
  supply: number;
  demand: number;
  gap: number;
}> {
  // 按年份分组
  const yearData: Record<number, { supply: number; demand: number }> = {};

  data.forEach(item => {
    if (!yearData[item.year]) {
      yearData[item.year] = { supply: 0, demand: 0 };
    }
    yearData[item.year].supply += item.supply_tons;
    yearData[item.year].demand += item.demand_tons;
  });

  // 转换为数组并计算缺口
  return Object.entries(yearData)
    .map(([year, { supply, demand }]) => ({
      year: parseInt(year),
      supply: Math.round(supply),
      demand: Math.round(demand),
      gap: Math.round(supply - demand),
    }))
    .sort((a, b) => a.year - b.year);
}

/**
 * 格式化供需缺口显示
 * 
 * @param gap 供需缺口（吨）
 * @returns 格式化的字符串
 */
export function formatSupplyDemandGap(gap: number): string {
  if (gap > 0) {
    return `供应过剩 ${gap.toLocaleString()} 吨`;
  } else if (gap < 0) {
    return `供应不足 ${Math.abs(gap).toLocaleString()} 吨`;
  } else {
    return '供需平衡';
  }
}

/**
 * 格式化库存消费比显示
 * 
 * @param ratio 库存消费比（月数）
 * @returns 格式化的字符串
 */
export function formatInventoryRatio(ratio: number): string {
  return `${ratio.toFixed(1)} 个月`;
}
